import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFilters } from '../../../../shared/contexts/filters/FiltersContext';
import { SOURCES } from '../../../../shared/routes/path';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { SourceStats } from '../../../../shared/types/monitor/data/sources/SourceStats';
import { SourceStatsWithComparison } from '../../hooks/UseSourceStatsComparison';
import { SearchFilter } from '../search-filter/SearchFilter';
import { PaginationData } from '../../../../shared/models/monitor/data/sources/PaginationData';
import {
  calculatePaginationValues,
  calculateSafeFirst,
  createPaginationHandler,
} from '../../../../shared/helpers/PaginationHelper';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import './SourcesStatsTable.scss';
import CustomLink from '../../../../shared/components/custom-link/CustomLink';
import { TrendTag } from '../../../projects/preview/components/trend-tag/TrendTag';
import { getTrendValue } from '../../../../shared/utils/utils';
import { Skeleton } from 'primereact/skeleton';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';
import { KeywordResponse } from '../../../../shared/models/monitor/KeywordResponse';
import { getKeywordMap } from '../../../prompt/preview/components/stats/helpers/StatsHelper';
import KeywordTags from '../../../../shared/components/keyword-tags/KeywordTags';
import { getVerifiedBrandStatus } from '../../helpers/SourcesHelper';
import { MetadataBadge } from '../../../../shared/components/metadata-badge/MetadataBadge';
import { Button } from 'primereact/button';
import useExport from '../../../../shared/hooks/global/UseExport';
import { TagsResponse } from '../../../../shared/models/tags/TagsResponse';
import { TAG_NAME, encodeSourceHash } from '../../../../shared/utils/urls';
import { getTagLabel } from '../../../../shared/helpers/tags/TagsHelper';

interface SourcesStatsTableProps {
  sources: SourceStatsWithComparison[];
  pagination?: PaginationData;
  onPageChange: (page: number, pageSize: number) => void;
  isLoading?: boolean;
  keyword?: string;
  onKeywordChange: (keyword: string) => void;
  startDate: Date | null;
  endDate: Date | null;
  previousStartDate: Date | null;
  previousEndDate: Date | null;
  hasAdvancedFeatures?: boolean;
  commonKeywords: KeywordResponse[];
  firmName: string;
  tags?: TagsResponse[];
  onExport: () => Promise<void>;
  hasSourceDetailPage?: boolean;
}

export const SourcesStatsTable: React.FC<SourcesStatsTableProps> = ({
  sources,
  pagination,
  onPageChange,
  isLoading = false,
  keyword = '',
  onKeywordChange,
  startDate,
  endDate,
  previousStartDate,
  previousEndDate,
  hasAdvancedFeatures = false,
  commonKeywords,
  firmName,
  tags = [],
  onExport,
  hasSourceDetailPage = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const { selectedChatType, monitorPeriod } = useFilters();
  const { canExport } = useExport();
  const [isDownloading, setIsDownloading] = useState(false);

  const handlePageChange = createPaginationHandler(pagination, onPageChange);

  const { first, rows, totalRecords } = calculatePaginationValues(pagination, sources.length);

  const safeFirst = calculateSafeFirst(first, rows, totalRecords);

  const keywordsMap = useMemo(() => {
    const map = getKeywordMap(commonKeywords);
    for (const kw of commonKeywords) {
      if (kw.normalizedKeyword.startsWith(TAG_NAME) && kw.tagId) {
        map.set(kw.normalizedKeyword, getTagLabel(tags, kw.tagId, kw.normalizedKeyword));
      }
    }
    return map;
  }, [commonKeywords, tags]);

  const formatDateRange = (start: Date | null, end: Date | null): string => {
    if (!start || !end) return '';
    return `${format(start, 'dd.MM')} - ${format(end, 'dd.MM.yyyy')}`;
  };

  const handleExport = async (): Promise<void> => {
    if (isDownloading || !canExport) {
      return;
    }

    setIsDownloading(true);
    try {
      await onExport();
    } finally {
      setTimeout(() => {
        setIsDownloading(false);
      }, 500);
    }
  };

  // Generate keys for columns with dates to force re-render when dates change
  const currentPeriodKey =
    startDate && endDate ? `${startDate.getTime()}-${endDate.getTime()}` : 'no-dates';
  const previousPeriodKey =
    previousStartDate && previousEndDate
      ? `${previousStartDate.getTime()}-${previousEndDate.getTime()}`
      : 'no-dates';

  return (
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('sourcesAnalytics')}
          <CustomTooltip tooltipText="sourcesAnalyticsTooltip" addMargin={false} />
        </div>
        <div className="page-box__source-filter">
          <SearchFilter value={keyword} onChange={onKeywordChange} className="search-filter--inline" />
          <>
            <Button
              id="btn-export-sources-stats"
              className={`action-btn ${!canExport ? 'btn--disabled-tooltip' : ''}`}
              onClick={handleExport}
            >
              {isDownloading ? (
                <span className="ico--loader ico--xs" />
              ) : (
                <span className={`ico--download ${!canExport ? 'ico--disabled' : ''} ico--xs`} />
              )}
            </Button>
            <CustomTooltip
              tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'downloadSources'}
              targetRef="btn-export-sources-stats"
            />
          </>
        </div>
      </div>

      <div className="sources-stats-table">
        {isLoading ? (
          <Skeleton className="skeleton--table">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </Skeleton>
        ) : (
          <DataTable
            value={sources}
            stripedRows
            tableClassName="table"
            className="table-wrapper"
            tableStyle={{ width: '100%' }}
            paginator
            lazy
            emptyMessage={() => <EmptyPanel />}
            paginatorClassName="pagination"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            rows={rows}
            first={safeFirst}
            onPage={handlePageChange}
            totalRecords={totalRecords}
            rowsPerPageOptions={[10, 25, 50]}
            dataKey="source"
            loading={isLoading}
            currentPageReportTemplate={t('rowsNumber') + ':'}
          >
            <Column
              field="source"
              header={t('source')}
              headerClassName="table__header"
              style={{ minWidth: '380px', maxWidth: '380px', width: 'auto' }}
              body={(rowData: SourceStats, options: any) => (
                <div className="source-data">
                  <span
                    className="source-data__icon"
                    style={{ backgroundImage: `url("https://favicon.is/${rowData.source}")` }}
                  />
                  <div className={`source-data__details${!hasSourceDetailPage ? ' source-data__details--basic' : ''}`}>
                    {hasAdvancedFeatures && rowData.title && (
                      hasSourceDetailPage ? (
                        <a
                          className="source-data__title source-data__title--clickable text-ellipsis"
                          onClick={(e) => {
                            e.stopPropagation();
                            const hash = encodeSourceHash(rowData.source);
                            const period = monitorPeriod?.toLowerCase() || 'daily';
                            const chat = selectedChatType?.toLowerCase() || 'all';
                            navigate(`${SOURCES}/${projectId}/source/${hash}/${period}/${chat}${location.search}`);
                          }}
                        >
                          {rowData.title}
                        </a>
                      ) : (
                        <span className="source-data__title text-ellipsis">{rowData.title}</span>
                      )
                    )}
                    <span
                      className={
                        hasAdvancedFeatures ? 'source-data__url' : ''
                      }
                      style={hasAdvancedFeatures && !rowData.title ? { marginTop: '2px' } : undefined}
                    >
                      {hasSourceDetailPage ? (
                        <span className="source-data__source-with-link">
                          <a
                            className="custom-link custom-link--clickable text-ellipsis"
                            onClick={(e) => {
                              e.stopPropagation();
                              const hash = encodeSourceHash(rowData.source);
                              const period = monitorPeriod?.toLowerCase() || 'daily';
                              const chat = selectedChatType?.toLowerCase() || 'all';
                              navigate(`${SOURCES}/${projectId}/source/${hash}/${period}/${chat}${location.search}`);
                            }}
                          >
                            {rowData.source}
                          </a>
                          <a
                            className="source-data__external-link"
                            href={rowData.source.startsWith('http') ? rowData.source : `https://${rowData.source}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="ico--sm ico--external-link" />
                          </a>
                        </span>
                      ) : (
                        <span className="text-ellipsis">
                          <CustomLink url={rowData.source} target="_blank" />
                        </span>
                      )}
                    </span>
                    {hasAdvancedFeatures && (
                      <KeywordTags
                        keywordsString={rowData.brands}
                        keywordsMap={keywordsMap}
                        containerWidth={360}
                        minVisibleKeywords={5}
                        uniqueId={`source-${options.rowIndex}`}
                      />
                    )}
                  </div>
                </div>
              )}
            />
            {hasAdvancedFeatures && (
              <Column
                field="isOwnBrandMentioned"
                header={
                  <div className="cell-header__title cell-header__title--wrap">
                    <div style={{ textTransform: 'capitalize', fontWeight: 600 }}>{firmName}</div>
                    <div>
                      <span id="sources-own-brand-header">{t('sourcesOwnBrand')}</span>
                      <CustomTooltip
                        tooltipText={t('sourcesOwnBrandHeaderTooltip', { firmName })}
                        targetRef="sources-own-brand-header"
                      />
                    </div>
                  </div>
                }
                headerClassName="table__header table__header--center"
                bodyClassName="table__cell--center"
                style={{ width: '80px', minWidth: '80px' }}
                body={(rowData: SourceStats, options: any) => {
                  const { variant, statusText, tooltipText } = getVerifiedBrandStatus(
                    rowData.isVerified,
                    rowData.brands && rowData.brands.length > 0 && rowData.brands.includes(firmName),
                    firmName,
                    t
                  );

                  return (
                    <MetadataBadge
                      variant={variant}
                      text={statusText}
                      tooltipText={tooltipText}
                      uniqueId={`brand-tooltip-${options.rowIndex}`}
                    />
                  );
                }}
              />
            )}
            <Column
              key={`previous-share-${previousPeriodKey}`}
              header={
                <div className="cell-header">
                  <div className="cell-header__title">
                    <span id="sources-prev-share-header">{t('sourcesAnalyticsShare')}</span>
                    <CustomTooltip
                      tooltipText="sourcesAnalyticsShareTooltip"
                      targetRef="sources-prev-share-header"
                    />
                  </div>
                  {previousStartDate && previousEndDate && (
                    <div className="cell-header__details">
                      {formatDateRange(previousStartDate, previousEndDate)}
                    </div>
                  )}
                </div>
              }
              headerClassName="table__header table__header--center table__header--relative"
              bodyClassName="table__cell--center table__cell--grey"
              style={{ width: '93px' }}
              body={(rowData) => `${rowData.previousShare.toFixed(1)}%`}
            />
            <Column
              key={`current-share-${currentPeriodKey}`}
              header={
                <div className="cell-header">
                  <div className="cell-header__title">
                    <span id="sources-curr-share-header">{t('sourcesAnalyticsShare')}</span>
                    <CustomTooltip
                      tooltipText="sourcesAnalyticsShareTooltip"
                      targetRef="sources-curr-share-header"
                    />
                  </div>
                  {startDate && endDate && (
                    <div className="cell-header__details">{formatDateRange(startDate, endDate)}</div>
                  )}
                </div>
              }
              headerClassName="table__header table__header--center table__header--relative"
              bodyClassName="table__cell--center"
              style={{ width: '93px' }}
              body={(rowData) => `${rowData.share.toFixed(1)}%`}
            />
            <Column
              header={
                <div className="cell-header__title">
                  <span id="sources-share-change-header">{t('sourcesAnalyticsShareChange')}</span>
                  <CustomTooltip
                    tooltipText="sourcesAnalyticsShareChangeTooltip"
                    targetRef="sources-share-change-header"
                  />
                </div>
              }
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center table__cell--right-border"
              style={{ width: '93px' }}
              body={(rowData) => (
                <TrendTag trendValue={getTrendValue(rowData.shareChange, 2)} suffix="%" />
              )}
            />
            <Column
              key={`previous-prompts-${previousPeriodKey}`}
              header={
                <div className="cell-header">
                  <div className="cell-header__title">
                    <span id="sources-prev-prompts-header">{t('sourcesAnalyticsPrompts')}</span>
                    <CustomTooltip
                      tooltipText="sourcesAnalyticsPromptsTooltip"
                      targetRef="sources-prev-prompts-header"
                    />
                  </div>
                  {previousStartDate && previousEndDate && (
                    <div className="cell-header__details">
                      {formatDateRange(previousStartDate, previousEndDate)}
                    </div>
                  )}
                </div>
              }
              headerClassName="table__header table__header--center table__header--relative"
              bodyClassName="table__cell--center table__cell--grey"
              style={{ width: '93px' }}
              body={(rowData) => rowData.previousPromptCount}
            />
            <Column
              key={`current-prompts-${currentPeriodKey}`}
              header={
                <div className="cell-header">
                  <div className="cell-header__title">
                    <span id="sources-curr-prompts-header">{t('sourcesAnalyticsPrompts')}</span>
                    <CustomTooltip
                      tooltipText="sourcesAnalyticsPromptsTooltip"
                      targetRef="sources-curr-prompts-header"
                    />
                  </div>
                  {startDate && endDate && (
                    <div className="cell-header__details">{formatDateRange(startDate, endDate)}</div>
                  )}
                </div>
              }
              headerClassName="table__header table__header--center table__header--relative"
              bodyClassName="table__cell--center"
              style={{ width: '93px' }}
              body={(rowData) => rowData.promptCount}
            />
            <Column
              header={
                <div className="cell-header__title">
                  <span id="sources-prompts-change-header">{t('sourcesAnalyticsPromptsChange')}</span>
                  <CustomTooltip
                    tooltipText="sourcesAnalyticsPromptsChangeTooltip"
                    targetRef="sources-prompts-change-header"
                  />
                </div>
              }
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              style={{ width: '73px' }}
              body={(rowData) => (
                <TrendTag trendValue={getTrendValue(rowData.promptCountChange)} suffix="" />
              )}
            />
          </DataTable>
        )}
      </div>
    </div>
  );
};
